import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EyeOff, Loader2, Lock, Pause, Play, RotateCcw, SkipForward } from 'lucide-react';

import { loadStorefrontReplay } from './api';
import { tx } from './i18n';
import { errorMessage } from './ui';

// A store session played back from what the store recorded, with rrweb's own
// player (the same version as the store's recorder). The player rebuilds each
// page in a sandboxed frame that runs no scripts. Every input was masked when
// it was recorded, and the payment and address areas were never recorded.
//
// The store is multi-page: every page load is a recording of its own, so a
// session is a run of them, played one after the other. The player is loaded
// only when a recording is opened.

const SPEEDS = [1, 2, 4, 8];
const MAX_HEIGHT = 560;
const PAGE_LIMIT = 20;

const text = (key, values) => tx(`ui.storefront.replay.${key}`, values);

function clockText(ms) {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

let playerModule = null;
export function loadPlayer() {
  if (!playerModule) {
    playerModule = Promise.all([import('@rrweb/replay'), import('@rrweb/replay/dist/style.css')])
      .then(([module]) => module.Replayer)
      .catch((error) => { playerModule = null; throw error; });
  }
  return playerModule;
}

// Every chunk of one page's recording, a page of chunks at a time, in order.
export async function readRecording(sessionId, page) {
  const events = [];
  let after = null;
  for (let round = 0; round < PAGE_LIMIT; round += 1) {
    // Pages of chunks come one after another; each asks for what follows the last.
    // eslint-disable-next-line no-await-in-loop
    const body = await loadStorefrontReplay(sessionId, { page, after });
    for (const chunk of body.chunks) events.push(...chunk.events);
    if (body.next === null || body.next === undefined) break;
    after = body.next;
  }
  return events.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Plays a session's recordings. `recordings` are its recorded page views in
 * order: { page, path, title, startedAt, markers: [{ at, kind, label }] }.
 */
export default function StorefrontReplay({ sessionId, recordings }) {
  const [index, setIndex] = useState(0);
  const [state, setState] = useState({ status: 'loading', error: null });
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [time, setTime] = useState(0);
  const [total, setTotal] = useState(0);
  const [viewport, setViewport] = useState({ width: 1280, height: 800 });
  const [width, setWidth] = useState(0);
  const stage = useRef(null);
  const box = useRef(null);
  const player = useRef(null);
  const cache = useRef(new Map());
  const autoplay = useRef(false);
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const recording = recordings[Math.min(index, recordings.length - 1)] || null;

  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // One page's recording into a fresh player; the next page follows when it ends.
  useEffect(() => {
    if (!recording) return undefined;
    let live = true;
    let replayer = null;
    setState({ status: 'loading', error: null });
    setTime(0);
    setTotal(0);
    const events = cache.current.has(recording.page)
      ? Promise.resolve(cache.current.get(recording.page))
      : readRecording(sessionId, recording.page).then((read) => { cache.current.set(recording.page, read); return read; });
    Promise.all([loadPlayer(), events]).then(([Replayer, read]) => {
      if (!live) return;
      if (read.length < 2) { setState({ status: 'empty', error: null }); return; }
      stage.current.replaceChildren();
      replayer = new Replayer(read, {
        root: stage.current, skipInactive: true, showWarning: false, triggerFocus: false, speed: speedRef.current,
        mouseTail: { duration: 600, lineWidth: 2, strokeStyle: 'rgba(29, 107, 251, .45)' },
      });
      player.current = replayer;
      replayer.on('resize', (size) => { if (live && size && size.width && size.height) setViewport({ width: size.width, height: size.height }); });
      replayer.on('finish', () => {
        if (!live) return;
        if (index + 1 < recordings.length) {
          autoplay.current = true;
          setIndex(index + 1);
        } else setPlaying(false);
      });
      setTotal(replayer.getMetaData().totalTime);
      setState({ status: 'ready', error: null });
      if (autoplay.current) { replayer.play(0); setPlaying(true); } else { replayer.pause(0); setPlaying(false); }
      autoplay.current = false;
    }).catch((error) => {
      if (live) setState({ status: 'failed', error: errorMessage(error) });
    });
    return () => {
      live = false;
      player.current = null;
      if (replayer) {
        try { replayer.destroy(); } catch { /* already torn down */ }
      }
    };
    // A new recording, or a new session, starts a new player; the speed is applied separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, recording ? recording.page : null]);

  useEffect(() => {
    if (player.current) player.current.setConfig({ speed });
  }, [speed]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      if (player.current) setTime(Math.min(player.current.getCurrentTime(), total));
    }, 200);
    return () => window.clearInterval(timer);
  }, [playing, total]);

  // The recorded page scaled to fit, never enlarged.
  useEffect(() => {
    const replayer = player.current;
    if (!replayer || !replayer.wrapper) return;
    const scale = width > 0 ? Math.min(1, width / viewport.width, MAX_HEIGHT / viewport.height) : 0.5;
    replayer.wrapper.style.transform = `scale(${scale})`;
    replayer.wrapper.style.transformOrigin = 'top left';
  }, [width, viewport, state.status]);

  const toggle = useCallback(() => {
    const replayer = player.current;
    if (!replayer) return;
    if (playing) {
      replayer.pause();
      setPlaying(false);
      return;
    }
    const from = time >= total - 50 ? 0 : replayer.getCurrentTime();
    replayer.play(from);
    setPlaying(true);
  }, [playing, time, total]);

  const seek = useCallback((offset) => {
    const replayer = player.current;
    if (!replayer) return;
    if (playing) replayer.play(offset); else replayer.pause(offset);
    setTime(offset);
  }, [playing]);

  const jump = useCallback((next) => {
    autoplay.current = playing;
    setIndex(next);
  }, [playing]);

  if (recordings.length === 0) {
    return (
      <div className="xo-sf-replay-empty">
        <EyeOff aria-hidden="true" />
        <strong>{text('none')}</strong>
        <span>{text('none_detail')}</span>
      </div>
    );
  }

  const scale = width > 0 ? Math.min(1, width / viewport.width, MAX_HEIGHT / viewport.height) : 0.5;
  const finished = !playing && total > 0 && time >= total - 50;
  const markers = recording && recording.markers && total > 0
    ? recording.markers.map((marker) => ({ ...marker, offset: marker.at - recording.startedAt })).filter((marker) => marker.offset >= 0 && marker.offset <= total)
    : [];

  return (
    <div className="xo-sf-replay">
      {recordings.length > 1 && (
        <ol className="xo-sf-replay-pages" aria-label={text('pages')}>
          {recordings.map((entry, position) => (
            <li key={entry.page}>
              <button type="button" className={position === index ? 'is-current' : position < index ? 'is-past' : ''} onClick={() => jump(position)}>
                <b>{position + 1}</b><span title={entry.path || '/'}>{entry.title || entry.path || '/'}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
      <div className="xo-sf-replay-bar">
        <span className="xo-sf-replay-dots" aria-hidden="true"><i /><i /><i /></span>
        <span className="xo-sf-replay-address"><Lock aria-hidden="true" />{recording ? recording.path || '/' : '/'}</span>
        <span className="xo-sf-replay-size">{viewport.width} × {viewport.height}</span>
      </div>
      <div className="xo-sf-replay-stage" ref={box}>
        <div className="xo-sf-replay-canvas" style={{ width: viewport.width * scale, height: viewport.height * scale }}>
          <div ref={stage} className="xo-sf-replay-root" />
          {state.status === 'loading' && <span className="xo-sf-replay-note"><Loader2 className="xo-spin" aria-hidden="true" />{text('loading')}</span>}
          {state.status === 'empty' && <span className="xo-sf-replay-note"><EyeOff aria-hidden="true" />{text('page_empty')}</span>}
          {state.status === 'failed' && <span className="xo-sf-replay-note is-bad">{state.error}</span>}
        </div>
      </div>
      <div className="xo-sf-replay-controls">
        <button type="button" className="xo-mob-play" onClick={toggle} disabled={state.status !== 'ready'}
          aria-label={playing ? tx('ui.mobile.pause') : tx('ui.mobile.play')}>
          {finished ? <RotateCcw aria-hidden="true" /> : playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </button>
        <div className="xo-sf-replay-track">
          <input type="range" min={0} max={Math.max(1, Math.round(total))} value={Math.round(Math.min(time, total))} disabled={state.status !== 'ready'}
            aria-label={tx('ui.mobile.timeline')} onChange={(event) => seek(Number(event.target.value))}
            style={{ '--xo-sf-progress': `${total > 0 ? (Math.min(time, total) / total) * 100 : 0}%` }} />
          <span className="xo-sf-replay-marks" aria-hidden="true">
            {markers.map((marker, position) => (
              <i key={`${marker.kind}-${position}`} className={`is-${marker.kind}`} title={marker.label}
                style={{ left: `${(marker.offset / total) * 100}%` }} />
            ))}
          </span>
        </div>
        <span className="xo-sf-replay-time">{clockText(Math.min(time, total))} / {clockText(total)}</span>
        <div className="xo-mob-speed" role="group" aria-label={tx('ui.mobile.speed')}>
          {SPEEDS.map((value) => (
            <button key={value} type="button" className={speed === value ? 'is-active' : ''} onClick={() => setSpeed(value)}>{value}×</button>
          ))}
        </div>
        {index + 1 < recordings.length && (
          <button type="button" className="xo-mob-icon-button" onClick={() => jump(index + 1)} aria-label={text('next_page')} title={text('next_page')}>
            <SkipForward />
          </button>
        )}
      </div>
      <p className="xo-mob-footnote">{text('privacy_note')}</p>
    </div>
  );
}
